export type GmailStatus = {
  enabled: boolean
  connected: boolean
}

export type SendEmailInput = {
  to: string
  subject: string
  /** Plain-text version; always sent, and the fallback part of the email. */
  body: string
  /** Formatted version from the composer. The server re-sanitizes it. */
  html?: string
}

export type SendEmailResult = {
  messageId: string
  threadId: string | null
}

/* Server codes the compose UI reacts to by name. */
export const GMAIL_REAUTHORIZATION_REQUIRED = 'GMAIL_REAUTHORIZATION_REQUIRED'

export class GmailRequestError extends Error {
  code: string

  constructor(code: string, message: string) {
    super(message)
    this.name = 'GmailRequestError'
    this.code = code
  }
}

async function readPayload(response: Response): Promise<Record<string, unknown>> {
  try {
    const payload = await response.json()

    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      return payload as Record<string, unknown>
    }
  } catch {
    // The caller reports the HTTP failure below.
  }

  return {}
}

function messageFrom(payload: Record<string, unknown>, fallback: string): string {
  return typeof payload.message === 'string' && payload.message ? payload.message : fallback
}

export type GmailOAuthMode = 'connect' | 'switch'

export const GMAIL_OAUTH_HOST_MESSAGE =
  'Gmail connection must be changed from localhost or williamstreasure.com.'

/*
 * Google rejects OAuth redirects to private LAN addresses (192.168.*, 10.*,
 * 172.16-31.*). Same rule as the server's start endpoint.
 */
export function isPrivateLanHost(hostname: string): boolean {
  const match = /^(\d{1,3})\.(\d{1,3})\.\d{1,3}\.\d{1,3}$/.exec(hostname)

  if (!match) return false

  const [a, b] = [Number(match[1]), Number(match[2])]

  return a === 10 || (a === 192 && b === 168) || (a === 172 && b >= 16 && b <= 31)
}

/**
 * Starts Google authorization by top-level navigation. Returns false without
 * navigating on a private LAN host. A switch keeps the current account
 * connected until Google returns a new one.
 */
export function connectGmail(mode: GmailOAuthMode = 'connect'): boolean {
  if (isPrivateLanHost(window.location.hostname)) return false

  window.location.assign(mode === 'switch' ? '/api/google/oauth/start?mode=switch' : '/api/google/oauth/start')
  return true
}

export async function loadGmailStatus(): Promise<GmailStatus> {
  const response = await fetch('/api/gmail/status', {
    credentials: 'include',
    headers: { accept: 'application/json' },
  })

  const payload = await readPayload(response)

  if (!response.ok) {
    throw new GmailRequestError(
      'GMAIL_STATUS_UNAVAILABLE',
      messageFrom(payload, 'Gmail connection status could not be loaded.'),
    )
  }

  return {
    enabled: payload.enabled === true,
    connected: payload.connected === true,
  }
}

export async function sendGmailEmail(input: SendEmailInput): Promise<SendEmailResult> {
  const response = await fetch('/api/gmail/send', {
    method: 'POST',
    credentials: 'include',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
    },
    body: JSON.stringify(input),
  })

  const payload = await readPayload(response)

  if (!response.ok || payload.sent !== true || typeof payload.messageId !== 'string') {
    throw new GmailRequestError(
      typeof payload.code === 'string' ? payload.code : 'GMAIL_SEND_FAILED',
      messageFrom(payload, 'The email could not be sent.'),
    )
  }

  return {
    messageId: payload.messageId,
    threadId: typeof payload.threadId === 'string' ? payload.threadId : null,
  }
}
